import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faEyeSlash, faLock } from '@fortawesome/free-solid-svg-icons';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import LoginForm from '../account/LoginForm';
import KnowledgeBaseLayout from './KnowledgeBaseLayout';
import { fetchPublicKnowledgeBase } from './knowledgeBaseApi';
import { KnowledgeBaseAvatar } from './KnowledgeBaseCards';
import { KnowledgeBaseContactPrompt } from './KnowledgeBaseContactForm';
import { KnowledgeBaseArticleListItem, PublicKnowledgeBaseArticle, PublicKnowledgeBaseGroup, KNOWLEDGE_BASE_PUBLIC_PATH, describeAuthors, describeUpdatedAgo, resolveMediaUrl } from './KnowledgeBaseTypes';

// The page of an article in the public knowledge base, restricted articles fade out into a login panel and managers preview concepts
const KnowledgeBaseArticlePage: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { isLoggedIn } = useAuthContext();
    const [article, setArticle] = useState<PublicKnowledgeBaseArticle | null>(null);
    const [group, setGroup] = useState<PublicKnowledgeBaseGroup | null>(null);
    const [notFound, setNotFound] = useState(false);

    // Fetch the article, again after a login so the full text is loaded
    useEffect(() => {
        setNotFound(false);
        fetchPublicKnowledgeBase<PublicKnowledgeBaseArticle>(`get_public_knowledge_base_article/${id}`)
            .then(setArticle)
            .catch(() => setNotFound(true));
    }, [id, isLoggedIn]);

    // Fetch the group of the article for the previous and next links
    const groupId = article?.group ?? null;
    useEffect(() => {
        if (!groupId) return;
        fetchPublicKnowledgeBase<PublicKnowledgeBaseGroup>(`get_public_knowledge_base_group/${groupId}`)
            .then(setGroup)
            .catch(() => setGroup(null));
    }, [groupId, isLoggedIn]);

    // The articles of the group in reading order and the neighbours of the current article
    const { previousArticle, nextArticle } = useMemo(() => {
        const ordered: KnowledgeBaseArticleListItem[] = group?.id === groupId ? group.sections.flatMap(section => section.articles) : [];
        const index = ordered.findIndex(item => item.id === Number(id));
        return {
            previousArticle: index > 0 ? ordered[index - 1] : null,
            nextArticle: index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null,
        };
    }, [group, groupId, id]);

    // Sanitize the article html and resolve the image urls for local environments
    const articleHtml = useMemo(() => {
        if (!article?.text) return '';
        const clean = DOMPurify.sanitize(article.text, { ALLOWED_TAGS: ['p', 'h1', 'h2', 'a', 'blockquote', 'img', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li'], ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt'] });
        return clean.replace(/src="(\/[^"]+)"/g, (_match: string, url: string) => `src="${resolveMediaUrl(url)}"`);
    }, [article]);

    return (
        <KnowledgeBaseLayout>
            <nav className='kb-breadcrumb'>
                <Link to={KNOWLEDGE_BASE_PUBLIC_PATH}>{t('knowledge_base.public.all_groups')}</Link>
                {article && (
                    <>
                        <span className='kb-breadcrumb-separator'>/</span>
                        <Link to={`${KNOWLEDGE_BASE_PUBLIC_PATH}/groep/${article.group}`}>{article.group_name}</Link>
                        {article.section_name && <><span className='kb-breadcrumb-separator'>/</span><span>{article.section_name}</span></>}
                    </>
                )}
            </nav>

            {notFound && <div className='kb-empty-state'>{t('knowledge_base.public.not_found')}</div>}

            {article && (
                <article className='kb-article'>
                    {article.status === 'concept' && (
                        <div className='kb-concept-notice'>
                            <span className='kb-concept-notice-badge'>{t('knowledge_base.article.status_concept')}</span>
                            <FontAwesomeIcon icon={faEyeSlash} />
                            <span>{t('knowledge_base.public.concept_preview_notice')}</span>
                        </div>
                    )}
                    {article.packages.length > 0 && (
                        <div className='kb-package-tags'>
                            {article.packages.map(name => <span key={name} className='kb-package-tag'>{name}</span>)}
                        </div>
                    )}
                    <h1 className='kb-article-title'>{article.subject}</h1>
                    {article.description && <p className='kb-article-description'>{article.description}</p>}
                    <div className='kb-article-author'>
                        {article.author && <KnowledgeBaseAvatar author={article.author} />}
                        <div className='kb-article-author-text'>
                            {article.author && <div className='kb-article-written-by'>{describeAuthors([article.author], t)}</div>}
                            <div className='kb-article-updated'>{describeUpdatedAgo(article.modified, t)}</div>
                        </div>
                    </div>

                    <div className={`kb-article-body ${article.is_truncated ? 'kb-article-body-truncated' : ''}`} dangerouslySetInnerHTML={{ __html: articleHtml }} />

                    {article.is_truncated && (
                        <div className='kb-login-panel'>
                            <div className='kb-login-panel-icon'><FontAwesomeIcon icon={faLock} /></div>
                            <h2>{t('knowledge_base.public.login_title')}</h2>
                            <p>{t('knowledge_base.public.login_text')}</p>
                            <div className='kb-login-form'>
                                <LoginForm />
                            </div>
                        </div>
                    )}

                    <KnowledgeBaseContactPrompt />

                    {(previousArticle || nextArticle) && (
                        <nav className='kb-article-nav'>
                            {previousArticle && (
                                <Link to={`${KNOWLEDGE_BASE_PUBLIC_PATH}/artikel/${previousArticle.id}`} className='kb-article-nav-previous'>
                                    <FontAwesomeIcon icon={faAngleLeft} /> {previousArticle.subject}
                                </Link>
                            )}
                            {nextArticle && (
                                <Link to={`${KNOWLEDGE_BASE_PUBLIC_PATH}/artikel/${nextArticle.id}`} className='kb-article-nav-next'>
                                    {nextArticle.subject} <FontAwesomeIcon icon={faAngleRight} />
                                </Link>
                            )}
                        </nav>
                    )}
                </article>
            )}
        </KnowledgeBaseLayout>
    );
};

export default KnowledgeBaseArticlePage;
